import { normalizeResolvedPath } from '../../../paths/normalize-resolved-path.js';
import { isBridgeBrowseInteractiveActive, registerFileBrowserActivityListener } from '../../../files/browser/in-flight.js';
import { createCodeNavYieldState, yieldDuringCodeNavWork } from '../../yield-during-work.js';
import { createWalkYieldState, walkCodeNavFilesAsync } from '../../walk-code-nav-files.js';
import { loadSymbolIndexReadyFileSet } from '../lifecycle/symbol-index-file-ready.js';
import { enqueueSymbolIndexTaskInternal } from './task-queue-core.js';
import { kickSymbolIndexWorkerLoop } from './task-queue-worker.js';

let requestSeq = 0;
let warmupSeq = 0;
const warmupCompleted = new Set<string>();
const pendingWarmupParents = new Set<string>();
const warmupWalkInProgress = new Set<string>();

/** Delay warmup until the user stops browsing files for this long. */
const WARMUP_IDLE_MS = 3_000;

let warmupIdleTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleWarmupWhenIdle(): void {
  if (warmupIdleTimer != null) clearTimeout(warmupIdleTimer);
  warmupIdleTimer = setTimeout(() => {
    warmupIdleTimer = null;
    if (isBridgeBrowseInteractiveActive()) {
      scheduleWarmupWhenIdle();
      return;
    }
    for (const parentPath of [...pendingWarmupParents]) {
      void runWarmupWalk(parentPath);
    }
  }, WARMUP_IDLE_MS);
}

registerFileBrowserActivityListener(() => {
  if (pendingWarmupParents.size > 0) scheduleWarmupWhenIdle();
});

async function runWarmupWalk(resolved: string): Promise<void> {
  if (warmupCompleted.has(resolved) || warmupWalkInProgress.has(resolved)) {
    pendingWarmupParents.delete(resolved);
    return;
  }
  warmupWalkInProgress.add(resolved);
  pendingWarmupParents.delete(resolved);
  try {
    if (isBridgeBrowseInteractiveActive()) {
      pendingWarmupParents.add(resolved);
      scheduleWarmupWhenIdle();
      return;
    }
    const readyFiles = await loadSymbolIndexReadyFileSet(resolved);
    const walkState = createWalkYieldState();
    const yieldState = createCodeNavYieldState();
    await walkCodeNavFilesAsync(
      resolved,
      async (relPath) => {
        await yieldDuringCodeNavWork(yieldState);
        if (!readyFiles.has(relPath)) {
          if (enqueueSymbolIndexTaskInternal(resolved, relPath, ++warmupSeq, requestSeq)) {
            kickSymbolIndexWorkerLoop();
          }
        }
      },
      walkState,
    );
    warmupCompleted.add(resolved);
  } finally {
    warmupWalkInProgress.delete(resolved);
  }
}

/** Low-priority background fill for remaining unindexed files after browse idle. */
export function scheduleSymbolIndexWarmupInWorkerHost(parentPath: string): void {
  const resolved = normalizeResolvedPath(parentPath);
  if (warmupCompleted.has(resolved)) return;
  pendingWarmupParents.add(resolved);
  scheduleWarmupWhenIdle();
}

/** @internal Exposed for tests. */
export function resetSymbolIndexWarmupStateForTests(): void {
  warmupCompleted.clear();
  pendingWarmupParents.clear();
  warmupWalkInProgress.clear();
  if (warmupIdleTimer != null) {
    clearTimeout(warmupIdleTimer);
    warmupIdleTimer = null;
  }
}

export function bumpSymbolIndexWarmupRequestSeq(): void {
  requestSeq += 1;
}
